export class WebRTCService {
  constructor() {
    this.peerConnection = null;
    this.localStream = null;
    this.remoteAudio = new Audio();
    this.remoteAudio.autoplay = true;
    this.onRemoteStreamCallback = null;
  }

  async initializePeerConnection() {
    // STUN servers configuration
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    };

    this.peerConnection = new RTCPeerConnection(configuration);

    // Get user media (microphone)
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      this.localStream.getTracks().forEach((track) => {
        if (this.peerConnection) {
          this.peerConnection.addTrack(track, this.localStream);
        }
      });
    } catch (error) {
      console.error('Error accessing media devices:', error);
      throw error;
    }

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      console.log('Received remote stream');
      const remoteStream = event.streams[0];

      if (this.remoteAudio) {
        this.remoteAudio.srcObject = remoteStream;
      }

      if (this.onRemoteStreamCallback) {
        this.onRemoteStreamCallback(remoteStream);
      }
    };

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('New ICE candidate:', event.candidate);
        // 👉 IMPORTANT: yahan se candidate signaling server ko bhejna hota hai
      } else {
        console.log('All ICE candidates have been sent');
      }
    };

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      console.log(
        'Connection state:',
        this.peerConnection && this.peerConnection.connectionState
      );
    };
  }

  async createAnswer(offerSdp) {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialized');
    }

    try {
      await this.peerConnection.setRemoteDescription({
        type: 'offer',
        sdp: offerSdp,
      });

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      return answer.sdp || '';
    } catch (error) {
      console.error('Error creating answer:', error);
      throw error;
    }
  }

  async createOffer() {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialized');
    }

    try {
      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      });

      await this.peerConnection.setLocalDescription(offer);
      return offer.sdp || '';
    } catch (error) {
      console.error('Error creating offer:', error);
      throw error;
    }
  }

  async handleAnswer(answerSdp) {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialized');
    }

    try {
      await this.peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });
    } catch (error) {
      console.error('Error handling answer:', error);
      throw error;
    }
  }

  setAudioTrackEnabled(enabled) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }

    if (this.peerConnection) {
      this.peerConnection.getSenders().forEach((sender) => {
        if (sender?.track?.kind === 'audio') {
          sender.track.enabled = enabled;
        }
      });
    }
  }

  muteAudio() {
    this.setAudioTrackEnabled(false);
  }

  unmuteAudio() {
    this.setAudioTrackEnabled(true);
  }

  isAudioMuted() {
    if (this.localStream) {
      const audioTracks = this.localStream.getAudioTracks();
      return audioTracks.length > 0 ? !audioTracks[0].enabled : true;
    }

    if (this.peerConnection) {
      const sender = this.peerConnection
        .getSenders()
        .find((item) => item?.track?.kind === 'audio');

      if (sender?.track) {
        return !sender.track.enabled;
      }
    }

    return true;
  }

  setRemoteStreamCallback(callback) {
    this.onRemoteStreamCallback = callback;
  }

  disconnect() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.remoteAudio) {
      this.remoteAudio.pause();
      this.remoteAudio.srcObject = null;
    }

    console.log('WebRTC connection disconnected');
  }

  getConnectionState() {
    return this.peerConnection ? this.peerConnection.connectionState : null;
  }
}
